import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  ChevronLeft,
  ChevronRight,
  X,
  Check,
  HelpCircle,
  Crown,
  Lock,
} from 'lucide-react';
import { cn, formatResetTime } from '@/lib/utils';
import { useUsageStats } from '@/hooks/useUsageStats';
import { useAuth } from '@/hooks/useClerkAuth';
import {
  getRemainingTrialQuestions,
  TRIAL_QUESTIONS_LIMIT,
} from '@/services/trialTracker';

export interface QuestionWithAnswers {
  id: string;
  questionNumber: number;
  germanQuestion: string;
  englishQuestion?: string | null;
  vietnameseQuestion?: string | null;
  imageUrl?: string | null;
  answers: {
    id: string;
    answerIndex: number;
    germanAnswer: string;
    englishAnswer?: string | null;
    vietnameseAnswer?: string | null;
    isCorrect: boolean;
  }[];
}

interface QuestionReaderProps {
  questions: QuestionWithAnswers[];
  onClose: () => void;
  catalogName?: string;
  catalogId?: string;
  isPracticeMode?: boolean;
  onAnswerSubmit?: (
    questionId: string,
    selectedAnswerId: string,
    isCorrect: boolean,
    questionNumber: number
  ) => void;
  startingIndex?: number;
  hasProAccess?: boolean;
  remainingQuestions?: number;
  remainingTrialQuestions?: number;
}

const QuestionReader = ({
  questions,
  onClose,
  catalogName,
  catalogId,
  isPracticeMode = false,
  onAnswerSubmit,
  startingIndex = 0,
  hasProAccess = true,
  remainingQuestions = 0,
  remainingTrialQuestions: remainingTrialQuestionsProp,
}: QuestionReaderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { usageStats } = useUsageStats(catalogId || '');
  const [currentIndex, setCurrentIndex] = useState(startingIndex);
  const [showEnglish, setShowEnglish] = useState(false);
  const [showVietnamese, setShowVietnamese] = useState(false);
  const [selectedAnswerId, setSelectedAnswerId] = useState<string | null>(null);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [userAnswers, setUserAnswers] = useState<
    Record<string, { selectedId: string; isCorrect: boolean }>
  >({});
  const [showTrialPrompt, setShowTrialPrompt] = useState(false);

  // Use prop value if provided, otherwise get from localStorage
  const remainingTrialQuestions =
    remainingTrialQuestionsProp ?? getRemainingTrialQuestions();

  const currentQuestion = questions[currentIndex];
  const currentQuestionAnswered = userAnswers[currentQuestion.id];

  const goToPrevious = useCallback(() => {
    // Check rate limiting for authenticated free users only (not trial users)
    if (user && !hasProAccess && remainingQuestions <= 0) {
      navigate('/pricing');
      return;
    }
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setSelectedAnswerId(null);
      setHasAnswered(false);
    }
  }, [currentIndex, hasProAccess, remainingQuestions, navigate, user]);

  const goToNext = useCallback(() => {
    // Check rate limiting for authenticated free users only (not trial users)
    if (user && !hasProAccess && remainingQuestions <= 0) {
      navigate('/pricing');
      return;
    }
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswerId(null);
      setHasAnswered(false);
    }
  }, [
    currentIndex,
    questions.length,
    hasProAccess,
    remainingQuestions,
    navigate,
    user,
  ]);

  const handleAnswerSelect = (answerId: string) => {
    if (!isPracticeMode || hasAnswered || currentQuestionAnswered) return;
    setSelectedAnswerId(answerId);
  };

  const handleSubmitAnswer = useCallback(() => {
    if (!selectedAnswerId || hasAnswered) return;

    const selectedAnswer = currentQuestion.answers.find(
      a => a.id === selectedAnswerId
    );
    if (!selectedAnswer) return;

    const isCorrect = selectedAnswer.isCorrect;
    setHasAnswered(true);

    setUserAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: {
        selectedId: selectedAnswerId,
        isCorrect,
      },
    }));

    // Let parent component handle trial consumption and rate limiting
    onAnswerSubmit?.(
      currentQuestion.id,
      selectedAnswerId,
      isCorrect,
      currentQuestion.questionNumber
    );
  }, [selectedAnswerId, hasAnswered, currentQuestion, onAnswerSubmit]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Check rate limiting for authenticated free users only (not trial users)
      const isRateLimited = user && !hasProAccess && remainingQuestions <= 0;
      if (e.key === 'ArrowLeft' && !isRateLimited) goToPrevious();
      if (e.key === 'ArrowRight' && !isRateLimited) goToNext();
      if (e.key === 'Escape') onClose();

      // Number keys for answer selection
      if (isPracticeMode && !hasAnswered && !currentQuestionAnswered) {
        const num = parseInt(e.key);
        if (num >= 1 && num <= 4) {
          const answer = currentQuestion.answers.find(
            a => a.answerIndex === num
          );
          if (answer) {
            setSelectedAnswerId(answer.id);
          }
        }
        if (e.key === 'Enter' && selectedAnswerId) {
          handleSubmitAnswer();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [
    currentIndex,
    onClose,
    selectedAnswerId,
    hasAnswered,
    currentQuestionAnswered,
    isPracticeMode,
    currentQuestion.answers,
    goToNext,
    goToPrevious,
    handleSubmitAnswer,
  ]);

  // Reset selection when question changes
  useEffect(() => {
    setSelectedAnswerId(null);
    setHasAnswered(false);
  }, [currentIndex]);

  const getAnswerButtonClass = (answer: { id: string; isCorrect: boolean }) => {
    const baseClass =
      'p-4 text-left border-2 transition-all duration-200 rounded-lg w-full';

    if (!isPracticeMode) {
      return cn(
        baseClass,
        'border-border hover:border-learning-primary/40 hover:bg-learning-primary/5'
      );
    }

    const isSelected = selectedAnswerId === answer.id;
    const hasUserAnswered = hasAnswered || currentQuestionAnswered;
    const userSelectedThis = currentQuestionAnswered?.selectedId === answer.id;

    if (hasUserAnswered) {
      if (answer.isCorrect) {
        return cn(baseClass, 'border-green-500 bg-green-50 text-green-800');
      }
      if (userSelectedThis && !answer.isCorrect) {
        return cn(baseClass, 'border-red-500 bg-red-50 text-red-800');
      }
      return cn(baseClass, 'border-border opacity-60');
    }

    if (isSelected) {
      return cn(
        baseClass,
        'border-learning-primary bg-learning-primary/10 text-learning-primary'
      );
    }

    return cn(
      baseClass,
      'border-border hover:border-learning-primary/40 hover:bg-learning-primary/5'
    );
  };

  const getAnswerIcon = (answer: { id: string; isCorrect: boolean }) => {
    if (!isPracticeMode) return null;

    const hasUserAnswered = hasAnswered || currentQuestionAnswered;
    const userSelectedThis = currentQuestionAnswered?.selectedId === answer.id;

    if (hasUserAnswered) {
      if (answer.isCorrect) {
        return <Check className="h-5 w-5 text-green-600" />;
      }
      if (userSelectedThis && !answer.isCorrect) {
        return <X className="h-5 w-5 text-red-600" />;
      }
    }

    return null;
  };

  // Trial prompt modal
  if (showTrialPrompt) {
    return (
      <div className="fixed inset-0 bg-background z-50 flex items-center justify-center p-6">
        <Card className="max-w-md mx-auto">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-learning-primary" />
              {t('trialLimit.trialComplete')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              {t('trialLimit.questionsLimitReached', {
                limit: TRIAL_QUESTIONS_LIMIT,
              })}
            </p>
            <Alert>
              <Lock className="h-4 w-4" />
              <AlertDescription>
                {t('trialLimit.signUpToContinue')}
              </AlertDescription>
            </Alert>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => navigate('/sign-up')}
                className="bg-learning-primary hover:bg-learning-primary/90"
              >
                <Crown className="h-4 w-4 mr-2" />
                {t('auth.signUpFree')}
              </Button>
              <Button variant="outline" onClick={() => navigate('/sign-in')}>
                {t('auth.signIn')}
              </Button>
              <Button variant="ghost" onClick={onClose} className="mt-2">
                {t('questionReader.close')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-background z-50 flex flex-col">
      {/* Header */}
      <div className="bg-gradient-hero p-4 text-primary-foreground">
        <div className="container max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-primary-foreground hover:bg-white/20"
            >
              <X className="h-4 w-4 mr-2" />
              {t('questionReader.close')}
            </Button>
            <div className="flex items-center gap-4">
              <div className="text-sm opacity-90">
                {t('questionReader.questionOf', {
                  current: currentQuestion.questionNumber,
                  total: questions.length,
                })}
                {catalogName && <span className="ml-2">• {catalogName}</span>}
              </div>
              {/* {!user && remainingTrialQuestions > 0 && (
                <Badge
                  variant="secondary"
                  className="bg-white/20 text-white text-xs"
                >
                  {t('trialLimit.trialRemaining', {
                    count: remainingTrialQuestions,
                  })}
                </Badge>
              )} */}
            </div>
          </div>

          {/* Language toggles */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="english"
                checked={showEnglish}
                onCheckedChange={checked => setShowEnglish(checked === true)}
                className="border-white data-[state=checked]:bg-white data-[state=checked]:text-primary"
              />
              <label htmlFor="english" className="text-sm font-medium">
                {t('questionReader.english')}
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="vietnamese"
                checked={showVietnamese}
                onCheckedChange={checked => setShowVietnamese(checked === true)}
                className="border-white data-[state=checked]:bg-white data-[state=checked]:text-primary"
              />
              <label htmlFor="vietnamese" className="text-sm font-medium">
                {t('questionReader.vietnamese')}
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        <div className="container max-w-4xl mx-auto p-6">
          <div className="space-y-8 animate-fade-in">
            {/* Question Image */}
            {currentQuestion.imageUrl && (
              <div className="text-center">
                <div className="inline-block bg-white/50 p-4 rounded-xl shadow-lg border border-learning-muted/20">
                  <img
                    src={currentQuestion.imageUrl}
                    alt={`Question ${currentQuestion.questionNumber} illustration`}
                    className="max-w-full h-auto mx-auto rounded-lg max-h-64 object-contain"
                    loading="lazy"
                  />
                </div>
              </div>
            )}

            {/* Question Text */}
            <div className="text-center space-y-6">
              {/* German (always shown) */}
              <div>
                <div className="text-sm font-medium text-learning-primary mb-3 flex items-center justify-center gap-2">
                  <HelpCircle className="h-4 w-4" />
                  {t('questionReader.deutsch')}
                </div>
                <p className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground leading-relaxed">
                  {currentQuestion.germanQuestion}
                </p>
              </div>

              {/* English */}
              {showEnglish && currentQuestion.englishQuestion && (
                <div className="animate-slide-in">
                  <div className="text-sm font-medium text-learning-secondary mb-3">
                    {t('questionReader.english')}
                  </div>
                  <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                    {currentQuestion.englishQuestion}
                  </p>
                </div>
              )}

              {/* Vietnamese */}
              {showVietnamese && currentQuestion.vietnameseQuestion && (
                <div className="animate-slide-in">
                  <div className="text-sm font-medium text-learning-accent mb-3">
                    {t('questionReader.vietnamese')}
                  </div>
                  <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                    {currentQuestion.vietnameseQuestion}
                  </p>
                </div>
              )}
            </div>

            {/* Answers */}
            <Card className="border-learning-muted/30">
              <CardContent className="p-6">
                <div className="space-y-4">
                  {currentQuestion.answers
                    .sort((a, b) => a.answerIndex - b.answerIndex)
                    .map(answer => (
                      <button
                        key={answer.id}
                        onClick={() => handleAnswerSelect(answer.id)}
                        className={getAnswerButtonClass(answer)}
                        disabled={
                          !isPracticeMode ||
                          hasAnswered ||
                          !!currentQuestionAnswered
                        }
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 text-left">
                            <div className="flex items-start gap-3">
                              <Badge variant="outline" className="mt-1">
                                {String.fromCharCode(96 + answer.answerIndex)}
                              </Badge>
                              <div className="space-y-2 flex-1">
                                <div className="font-medium">
                                  {answer.germanAnswer}
                                </div>

                                {showEnglish && answer.englishAnswer && (
                                  <div className="text-sm text-muted-foreground">
                                    🇬🇧 {answer.englishAnswer}
                                  </div>
                                )}

                                {showVietnamese && answer.vietnameseAnswer && (
                                  <div className="text-sm text-muted-foreground">
                                    🇻🇳 {answer.vietnameseAnswer}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {getAnswerIcon(answer)}
                        </div>
                      </button>
                    ))}
                </div>

                {/* Submit button for practice mode */}
                {isPracticeMode &&
                  selectedAnswerId &&
                  !hasAnswered &&
                  !currentQuestionAnswered && (
                    <div className="mt-6 text-center">
                      <Button
                        onClick={handleSubmitAnswer}
                        size="lg"
                        className="bg-learning-primary"
                      >
                        {t('questionReader.submitAnswer')}
                      </Button>
                      <p className="text-sm text-muted-foreground mt-2">
                        {t('questionReader.pressEnter')}
                      </p>
                    </div>
                  )}

                {/* Result feedback */}
                {isPracticeMode && (hasAnswered || currentQuestionAnswered) && (
                  <div className="mt-6 p-4 rounded-lg bg-muted/50 text-center">
                    {currentQuestionAnswered?.isCorrect ? (
                      <div className="text-green-700">
                        <Check className="h-6 w-6 mx-auto mb-2" />
                        <p className="font-medium">
                          {t('questionReader.correct')}
                        </p>
                      </div>
                    ) : (
                      <div className="text-red-700">
                        <X className="h-6 w-6 mx-auto mb-2" />
                        <p className="font-medium">
                          {t('questionReader.incorrect')}
                        </p>
                        <p className="text-sm mt-1">
                          {t('questionReader.correctAnswerHighlighted')}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-muted p-4">
        <div className="container max-w-4xl mx-auto flex items-center justify-between">
          <Button
            variant="outline"
            onClick={goToPrevious}
            disabled={currentIndex === 0}
            className={`bg-white/50 backdrop-blur-sm ${
              user && !hasProAccess && remainingQuestions <= 0
                ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white border-transparent'
                : ''
            }`}
          >
            {user && !hasProAccess && remainingQuestions <= 0 ? (
              <Crown className="h-4 w-4 mr-2" />
            ) : (
              <ChevronLeft className="h-4 w-4 mr-2" />
            )}
            {user && !hasProAccess && remainingQuestions <= 0
              ? t('questionReader.upgradeToContinue')
              : t('questionReader.previous')}
          </Button>

          {/* Progress bar */}
          <div className="flex-1 mx-8">
            <div className="bg-learning-muted rounded-full h-2">
              <div
                className="bg-gradient-hero h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${((currentIndex + 1) / questions.length) * 100}%`,
                }}
              />
            </div>

            {/* Score display for practice mode */}
            {isPracticeMode && Object.keys(userAnswers).length > 0 && (
              <div className="text-center mt-2 text-sm text-muted-foreground">
                {t('questionReader.score')}{' '}
                {Object.values(userAnswers).filter(a => a.isCorrect).length} /{' '}
                {Object.keys(userAnswers).length}
              </div>
            )}
          </div>

          <Button
            variant="outline"
            onClick={goToNext}
            disabled={
              user && !hasProAccess && remainingQuestions <= 0
                ? false
                : currentIndex === questions.length - 1
            }
            className={`bg-white/50 backdrop-blur-sm ${
              user && !hasProAccess && remainingQuestions <= 0
                ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white border-transparent'
                : ''
            }`}
          >
            {user && !hasProAccess && remainingQuestions <= 0
              ? t('questionReader.upgradeToContinue')
              : t('questionReader.next')}
            {user && !hasProAccess && remainingQuestions <= 0 ? (
              <Crown className="h-4 w-4 ml-2" />
            ) : (
              <ChevronRight className="h-4 w-4 ml-2" />
            )}
          </Button>
        </div>
        {user && !hasProAccess && remainingQuestions <= 0 && (
          <div className="text-center text-sm text-muted-foreground mt-2">
            {t('questionReader.questionLimitReached')}
            {usageStats.resetDate &&
              ` ${t('questionReader.continuePracticingOn', {
                date: formatResetTime(usageStats.resetDate),
              })}`}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionReader;
